import {
	LibmapDataProvider,
	LibmapProvider,
	LibmapState,
} from '@lc/libmap-react'
import type { LibMap } from '@lc/libmap-types'
import { classes } from '@lc/libreact'
import { type PriorityExecutor } from '@lc/librunner'
import Plausible from 'plausible-tracker'
import { StrictMode, useEffect, useMemo, useState } from 'react'
import { Provider } from 'react-redux'
import { Router, type BaseLocationHook, type BaseSearchHook } from 'wouter'
import styles from './App.scss'
import { Board } from './components/Board/Board'
import './components/modules'
import { RouteActions } from './components/RouteActions'
import './components/ui'
import { NullLoading } from './components/ui/Loading'
import { ExecutorContext } from './contexts/executor'
import { SearchProvider } from './contexts/search'
import { TranslatorProvider, type LangListener } from './contexts/translator'
import './generated/assets'
import { LoadContext, type LoadListener } from './hooks/load/useLoad'
import { useLoadAnalytics } from './hooks/load/useLoadAnalytics'
import { useLoadConfigApiKeys } from './hooks/load/useLoadApiKeys'
import { useLoadAssets } from './hooks/load/useLoadAssets'
import { useLoadConfigModules } from './hooks/load/useLoadConfigModules'
import { useLoadConfigSpeed } from './hooks/load/useLoadConfigSpeed'
import { useLoadEntryMap } from './hooks/load/useLoadEntryMap'
import { useLoadMapConfig } from './hooks/load/useLoadMapConfig'
import { useClient } from './hooks/useClient'
import { makeUseCustomLocation } from './hooks/useCustomLocation'
import { useExecutor } from './hooks/useExecutor'
import { useMaybeModule } from './hooks/useModule'
import { useTranslator } from './hooks/useTranslator'
import {
	declareComponent,
	RegistryContext,
	type RegistryListener,
} from './registry'
import { getConfigAnalytics } from './stores/config'
import { useSelector } from './stores/hooks'
import {
	metaEffect,
	metaSetMobile,
	metaSetReallyMobile,
	metaSetTimezoneOffset,
} from './stores/meta'
import type { State, Store } from './stores/store'
import theme, { modeTheme } from './theme'

export const Map = /*#__PURE__*/ declareComponent(
	'map',
	'module-map',
	() => import(/* webpackChunkName: 'module-map' */ './components/Map'),
	NullLoading,
)

function loadLibmap(
	executor: PriorityExecutor,
	onLoad: (libmap: LibMap<unknown, unknown>) => void,
	{ level = 1 }: { level?: number } = {},
) {
	executor.schedule({ level, name: 'loadLibmap()' }, async () => {
		const libmap = await import(
			/* webpackChunkName: 'libmap-maplibre-gl' */
			'@lc/libmap-maplibre-gl'
		)
		onLoad(libmap)
	})
}

export function App() {
	const { status: assetsStatus } = useLoadAssets({ level: 0 })
	const { status: configModulesStatus } = useLoadConfigModules({ level: 0 })
	useLoadAnalytics({ level: 0 })
	useLoadEntryMap({ level: 2 })
	useLoadConfigApiKeys({ level: 2 })
	useLoadMapConfig({ level: 2 })
	useLoadConfigSpeed()
	const client = useClient()
	const module = useMaybeModule()
	const analytics = useSelector(getConfigAnalytics)
	const exe = useExecutor()

	useEffect(() => {
		const cleanup: (() => void)[] = []
		for (const analytic of analytics) {
			if (analytic.type === 'plausible') {
				const plausible = Plausible({
					domain: analytic.url_domain,
					apiHost: analytic.url_server,
				})
				cleanup.push(plausible.enableAutoPageviews())
			}
		}
		for (const fn of cleanup) fn()
	}, [analytics])

	const translator = useTranslator()
	const pageTitle = translator.translate('page-title')
	useEffect(() => {
		document.title = pageTitle
	}, [pageTitle])

	const [libmap, setLibmap] = useState<LibmapState<unknown, unknown> | null>(
		null,
	)
	const shouldLoadLibmapAlready = client && configModulesStatus === 'loaded'
	useEffect(() => {
		if (!shouldLoadLibmapAlready) return
		loadLibmap(exe, libmap => setLibmap(new LibmapState(libmap)), {
			level: 2,
		})
	}, [exe, shouldLoadLibmapAlready])

	if (assetsStatus !== 'loaded') return null

	return (
		<>
			<RouteActions />
			<main
				className={classes(
					module?.mode ? modeTheme(module.mode) : undefined,
					theme.global,
				)}
			>
				<div className={styles.skipLinkContainer}>
					Aller vers :
					<a href="#content" className={styles.skipLink}>
						Contenu
					</a>
					<a href="#search" className={styles.skipLink}>
						Rechercher
					</a>
					<a href="#mode" className={styles.skipLink}>
						Mode
					</a>
				</div>
				<LibmapDataProvider dataState={libmap?.data}>
					<Board />
				</LibmapDataProvider>
				{client && configModulesStatus === 'loaded' && (
					<LibmapProvider state={libmap}>
						<Map level={2} />
					</LibmapProvider>
				)}
			</main>
		</>
	)
}

export function AppStack({
	store,
	executor,
	preloadedState,
	ssrPath,
	ssrLang,
	ssrLangs,
	locationHook,
	searchHook,
	collectModules = null,
	collectLoad = null,
	collectLang = null,
}: {
	store: Store
	executor: PriorityExecutor
	preloadedState?: State
	ssrPath?: string
	ssrLang?: string
	ssrLangs?: string[]
	locationHook: BaseLocationHook
	searchHook: BaseSearchHook
	collectModules?: RegistryListener | null
	collectLoad?: LoadListener | null
	collectLang?: LangListener | null
}) {
	const actualLocationHook = useMemo(() => {
		return makeUseCustomLocation(locationHook, searchHook)
	}, [locationHook, searchHook])

	useEffect(() => {
		if (
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent,
			)
		) {
			store.dispatch(metaSetReallyMobile(true))
		}
	}, [store])

	useEffect(() => {
		const timezoneOffset = new Date().getTimezoneOffset()
		store.dispatch(metaSetTimezoneOffset(timezoneOffset))
	}, [store])

	useEffect(() => {
		const mobileMedia = window.matchMedia('(max-width: 768px)')
		store.dispatch(metaSetMobile(mobileMedia.matches))
		const handler = (e: MediaQueryListEvent) => {
			store.dispatch(metaSetMobile(e.matches))
		}
		mobileMedia.addEventListener('change', handler)
		return () => {
			mobileMedia.removeEventListener('change', handler)
		}
	}, [store])

	useEffect(() => {
		store.dispatch(metaEffect(new Date().toISOString()))
	}, [store])

	return (
		<StrictMode>
			<Router
				hook={actualLocationHook}
				searchHook={searchHook}
				ssrPath={ssrPath}
			>
				<Provider store={store} serverState={preloadedState}>
					<ExecutorContext value={executor}>
						<LoadContext value={collectLoad}>
							<TranslatorProvider
								ssrLang={ssrLang}
								ssrLangs={ssrLangs}
								collectLang={collectLang}
							>
								<RegistryContext value={collectModules}>
									<SearchProvider>
										<App />
									</SearchProvider>
								</RegistryContext>
							</TranslatorProvider>
						</LoadContext>
					</ExecutorContext>
				</Provider>
			</Router>
		</StrictMode>
	)
}
